import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDateRange } from "@/hooks/useDateRange";
import { exportElementToPdf } from "@/lib/exportPdf";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { Building2, MapPin, Layers, Landmark, Building, Loader2, Download } from "lucide-react";

const barColor = (v) => (v >= 75 ? "#059669" : v >= 50 ? "#f59e0b" : "#ef4444");

const rankBadge = (rank) =>
  rank === 1 ? "bg-amber-400 text-white" : rank === 2 ? "bg-slate-300 text-slate-800" : rank === 3 ? "bg-amber-700 text-white" : "bg-slate-100 text-slate-600";

const RankRow = ({ rank, title, subtitle, primary, secondary, testId }) => (
  <div className="flex items-center gap-3 px-4 py-3" data-testid={testId}>
    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold ${rankBadge(rank)}`}>{rank}</span>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-slate-800 truncate leading-6">{title}</p>
      {subtitle && <p className="text-xs text-slate-500 capitalize leading-5">{subtitle}</p>}
    </div>
    <div className="text-right">
      <p className="font-semibold text-emerald-700 tabular-nums">{primary}</p>
      {secondary && <p className="text-xs text-slate-500">{secondary}</p>}
    </div>
  </div>
);

const RankCard = ({ title, children, testId }) => (
  <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid={testId}>
    <div className="px-4 py-3 bg-emerald-600 text-white font-heading font-semibold text-sm capitalize">{typeof title === "string" ? title.toLowerCase() : title}</div>
    {children}
  </Card>
);

export default function Dashboard() {
  const { user, hasPerm } = useAuth();
  const [munis, setMunis] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assessmentWards, setAssessmentWards] = useState([]);
  const [scoring, setScoring] = useState({ ulb: [], ward: [], type: [] });
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pageRef = useRef(null);
  const { params: dateParams, controls: rangeControls } = useDateRange("dashboard-range");

  const downloadPdf = async () => {
    setExporting(true);
    try {
      await exportElementToPdf(pageRef.current, "smh-dashboard");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    api.get("/municipalities").then(({ data }) => setMunis(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = dateParams;
    const jobs = [];
    if (hasPerm("assessments.view")) {
      jobs.push(api.get("/rankings", { params }).then(({ data }) => { setCategories(data.categories || []); setAssessmentWards(data.wards || []); }).catch(() => {}));
    }
    if (hasPerm("monitoring.view")) {
      jobs.push(api.get("/scoring", { params }).then(({ data }) => setScoring(data)).catch(() => {}));
      jobs.push(api.get("/monitoring/overview", { params }).then(({ data }) => setOverview(data)).catch(() => {}));
    }
    if (!jobs.length) return;
    setLoading(true);
    Promise.all(jobs).finally(() => setLoading(false));
  }, [dateParams, hasPerm]);

  const districtCount = new Set(munis.map((m) => m.district)).size;
  const byType = (t) => munis.filter((m) => (m.mtype || "").toUpperCase() === t).length;
  const cards = [
    { label: "Total Districts", value: districtCount, icon: MapPin, color: "bg-emerald-600" },
    { label: "Total Municipalities", value: munis.length, icon: Building2, color: "bg-blue-600" },
    { label: "Municipal Corporation", value: byType("MUNICIPAL CORPORATION"), icon: Landmark, color: "bg-amber-500" },
    { label: "Municipal Council", value: byType("MUNICIPAL COUNCIL"), icon: Layers, color: "bg-slate-700" },
    { label: "Municipal Committee", value: byType("MUNICIPAL COMMITTEE"), icon: Building, color: "bg-teal-600" },
  ];

  const assessmentGroups = useMemo(() => {
    const pop = {};
    (munis || []).forEach((m) => {
      const nk = (m.name || "").trim().toLowerCase();
      if (pop[nk] === undefined) pop[nk] = m.population;
    });
    const getPop = (area) => {
      const v = pop[(area || "").trim().toLowerCase()];
      return v == null ? null : Number(v);
    };
    const rerank = (arr) => arr.map((r, i) => ({ ...r, rank: i + 1 }));
    const out = [];
    (categories || []).forEach((cat) => {
      const isCouncil = (cat.name || cat.label || "").toUpperCase().includes("COUNCIL");
      if (!isCouncil) {
        out.push({ key: cat.key, title: cat.name || cat.label, rows: cat.rows || [] });
        return;
      }
      const hi = [], lo = [];
      (cat.rows || []).forEach((r) => {
        const p = getPop(r.area);
        if (p != null && p >= 100000 && p <= 300000) hi.push(r);
        else lo.push(r);
      });
      out.push({ key: "council_high", title: "Municipal Council (Population between 1 Lakh to 3 Lakh)", rows: rerank(hi) });
      out.push({ key: "council_low", title: "Municipal Council (Population between 50000 to 1 Lakh)", rows: rerank(lo) });
    });
    const rankTitle = (t) => {
      const u = (t || "").toUpperCase();
      if (u.includes("CORPORATION")) return 0;
      if (u.includes("1 LAKH TO 3 LAKH")) return 1;
      if (u.includes("50000 TO 1 LAKH")) return 2;
      if (u.includes("COMMITTEE")) return 3;
      return 4;
    };
    return out.filter((g) => (g.rows || []).length > 0).sort((a, b) => rankTitle(a.title) - rankTitle(b.title));
  }, [categories, munis]);

  const assessmentTopMunis = useMemo(() => {
    const all = [];
    (categories || []).forEach((cat) => (cat.rows || []).forEach((r) => all.push({ ...r, _type: cat.name || cat.label })));
    all.sort((a, b) => (b.percent - a.percent) || (b.score - a.score));
    return all.slice(0, 3).map((r, i) => ({ ...r, rank: i + 1 }));
  }, [categories]);
  const chartData = useMemo(
    () =>
      (overview || []).map((m) => ({
        label: m.label,
        value: m.type === "status" ? (m.count ? Math.round((m.completed / m.count) * 100) : 0) : Math.round(m.avg_percentage ?? 0),
      })),
    [overview]
  );
  const topUlb = (scoring.ulb || []).slice(0, 3);
  const topWard = (scoring.ward || []).slice(0, 3);
  const topByType = useMemo(() => {
    const pop = {};
    (munis || []).forEach((m) => {
      const nk = (m.name || "").trim().toLowerCase();
      pop[`${nk}|${(m.district || "").trim().toLowerCase()}`] = m.population;
      if (pop[nk] === undefined) pop[nk] = m.population;
    });
    const getPop = (u) => {
      const nk = (u.municipality_name || "").trim().toLowerCase();
      const v = pop[`${nk}|${(u.district || "").trim().toLowerCase()}`] ?? pop[nk];
      return v == null ? null : Number(v);
    };
    const b = { corp: [], councilHigh: [], councilLow: [], comm: [] };
    (scoring.ulb || []).forEach((u) => {
      const t = (u.municipality_type || "").toUpperCase();
      if (t === "MUNICIPAL CORPORATION") b.corp.push(u);
      else if (t === "MUNICIPAL COMMITTEE") b.comm.push(u);
      else if (t === "MUNICIPAL COUNCIL") {
        const p = getPop(u);
        if (p != null && p >= 100000 && p <= 300000) b.councilHigh.push(u);
        else b.councilLow.push(u); // 50k–1L, plus any council with unknown/other population
      }
    });
    return [
      { type: "MUNICIPAL CORPORATION", rows: b.corp.slice(0, 3) },
      { type: "MUNICIPAL COUNCIL (Population between 1 Lakh to 3 Lakh)", rows: b.councilHigh.slice(0, 3) },
      { type: "MUNICIPAL COUNCIL (Population between 50000 to 1 Lakh)", rows: b.councilLow.slice(0, 3) },
      { type: "MUNICIPAL COMMITTEE", rows: b.comm.slice(0, 3) },
    ];
  }, [scoring.ulb, munis]);

  return (
    <div data-testid="dashboard-page" ref={pageRef}>
      <div className="flex justify-between items-start mb-8 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Overview</p>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Welcome, {user?.name}</h1>
          <p className="text-slate-500 mt-1">Swachhatam League Haryana — municipalities at a glance.</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap pdf-hide">
          {loading && <Loader2 className="h-5 w-5 animate-spin text-emerald-600 mb-2" data-testid="dashboard-loading" />}
          {rangeControls}
          <Button variant="outline" onClick={downloadPdf} disabled={exporting} data-testid="dashboard-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="ml-2">PDF</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {cards.map((c) => (
          <Card key={c.label} className="p-5 flex items-center gap-4 border-slate-200 shadow-sm" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`${c.color} rounded-lg p-3`}><c.icon className="h-6 w-6 text-white" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-heading">{c.value}</p>
              <p className="text-sm text-slate-500">{c.label}</p>
            </div>
          </Card>
        ))}
      </div>

      {hasPerm("monitoring.view") && (
        <div className={`mt-12 transition-opacity ${loading ? "opacity-40 pointer-events-none" : ""}`} data-testid="monitoring-rankings">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Monitoring Rankings</h2>
          <p className="text-sm text-slate-500 mb-5">Top performers across all monitoring modules.</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <RankCard title="Top 3 Municipalities" testId="rank-ulb-card">
              {topUlb.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">No data for this range.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {topUlb.map((r) => (
                    <RankRow key={`${r.district}-${r.municipality_name}`} rank={r.rank} title={r.municipality_name}
                      subtitle={`${(r.district || "—").toLowerCase()} · ${r.municipality_type || "—"}`}
                      primary={`${r.percent}%`} secondary={`${r.score} marks`} testId={`rank-ulb-${r.rank}`} />
                  ))}
                </div>
              )}
            </RankCard>

            <RankCard title="Top 3 Wards" testId="rank-ward-card">
              {topWard.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-slate-400">No data for this range.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {topWard.map((r) => (
                    <RankRow key={`${r.district}-${r.municipality_name}-${r.ward_no}`} rank={r.rank}
                      title={`${r.municipality_name} · Ward ${r.ward_no ?? "—"}`}
                      subtitle={(r.district || "—").toLowerCase()}
                      primary={`${r.percent}%`} secondary={`${r.score} marks`} testId={`rank-ward-${r.rank}`} />
                  ))}
                </div>
              )}
            </RankCard>
          </div>

          <h3 className="font-heading text-base font-semibold text-slate-800 mb-4">Top 3 Municipalities per Municipal Type</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {topByType.map((grp) => (
              <RankCard key={grp.type} title={grp.type || "—"} testId={`rank-type-${grp.type}`}>
                {grp.rows.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-slate-400">No data.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {grp.rows.map((r, i) => (
                      <RankRow key={r.municipality_name} rank={i + 1} title={r.municipality_name}
                        subtitle={(r.district || "—").toLowerCase()}
                        primary={`${r.percent}%`} secondary={`${r.score} marks`} testId={`rank-type-${grp.type}-${i + 1}`} />
                    ))}
                  </div>
                )}
              </RankCard>
            ))}
          </div>
        </div>
      )}

      {hasPerm("monitoring.view") && (
        <Card className={`mt-12 p-5 border-slate-200 shadow-sm transition-opacity ${loading ? "opacity-40 pointer-events-none" : ""}`} data-testid="monitoring-chart-card">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Average Performance by Monitoring Module</h2>
          <p className="text-sm text-slate-500 mb-4">State-wide average % achieved in each monitoring category for the selected period.</p>
          {chartData.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400" data-testid="chart-empty">No monitoring data for this range.</div>
          ) : (
            <ResponsiveContainer width="100%" height={chartData.length * 26 + 20}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 40, top: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis type="category" dataKey="label" width={190} tick={{ fontSize: 11, fill: "#334155" }} />
                <Tooltip formatter={(v) => [`${v}%`, "Avg"]} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={12} isAnimationActive={false}>
                  {chartData.map((e, i) => <Cell key={i} fill={barColor(e.value)} />)}
                  <LabelList dataKey="value" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 10, fill: "#475569" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      )}

      {hasPerm("assessments.view") && (
        <div className={`mt-12 transition-opacity ${loading ? "opacity-40 pointer-events-none" : ""}`} data-testid="assessment-rankings">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Rankings by Assessment</h2>
          <p className="text-sm text-slate-500 mb-5">Based on approved assessment scores.</p>

          {assessmentGroups.length === 0 ? (
            <Card className="border-slate-200 shadow-sm p-10 text-center text-sm text-slate-400">No approved assessments for this range.</Card>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <RankCard title="Top 3 Municipalities" testId="arank-muni-card">
                  {assessmentTopMunis.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">No data.</div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {assessmentTopMunis.map((r) => (
                        <RankRow key={r.area} rank={r.rank} title={r.area} subtitle={r._type}
                          primary={`${r.percent}%`} secondary={`${r.score}/${r.max}`} testId={`arank-muni-${r.rank}`} />
                      ))}
                    </div>
                  )}
                </RankCard>

                <RankCard title="Top 3 Wards" testId="arank-ward-card">
                  {assessmentWards.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">No data.</div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {assessmentWards.slice(0, 3).map((r) => (
                        <RankRow key={`${r.area}-${r.ward_no}`} rank={r.rank}
                          title={`${r.area} · Ward ${r.ward_no ?? "—"}`}
                          subtitle={(r.district || "—").toLowerCase()}
                          primary={`${r.percent}%`} secondary={`${r.score}/${r.max}`} testId={`arank-ward-${r.rank}`} />
                      ))}
                    </div>
                  )}
                </RankCard>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {assessmentGroups.map((grp) => (
                  <RankCard key={grp.key} title={grp.title} testId={`arank-type-${grp.key}`}>
                    <div className="divide-y divide-slate-100">
                      {grp.rows.slice(0, 3).map((r) => (
                        <RankRow key={r.area} rank={r.rank} title={r.area} subtitle={`${r.assessments} assessment(s)`}
                          primary={`${r.percent}%`} secondary={`${r.score}/${r.max}`} testId={`arank-${grp.key}-${r.rank}`} />
                      ))}
                    </div>
                  </RankCard>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
